import { useCallback, useEffect, useRef, useState } from 'react';
import { API } from '../lib/api';

/**
 * useTts — global TTS player. Reuses one Audio element so consecutive
 * calls don't overlap.
 *
 * Exposes THREE states so callers can render the right icon at each
 * phase without hacks:
 *   - `loading` — true only while we're POSTing to /api/tts + streaming
 *                 the MP3 blob. Flips to FALSE the moment playback
 *                 actually begins (Chrome/Safari fire `playing` after
 *                 the buffer is ready). This is what "spinner" callers
 *                 want; it must NOT stay true for the whole 20 s of
 *                 audio (iter-42 fix — the streaks / guidelines cards
 *                 were showing a stuck spinner throughout playback).
 *   - `playing` — true from playback start until the clip ends OR the
 *                 caller stops it. Callers render a Pause icon here.
 *   - `play()`  — still async; resolves when the clip FINISHES so
 *                 callers that want to chain (e.g. the goodbye flow)
 *                 keep working.
 */
export default function useTts(defaultSpeed = 0.75) {
  const audioRef = useRef(null);
  // Iter-51.33 — Monotonic play-sequence token. Each play() invocation
  // captures a fresh integer; if a newer play() has been triggered
  // before the older one reaches `audio.play()`, the older one aborts.
  // Blocks the double-audio-on-first-tap regression that surfaced when
  // React.StrictMode double-invoked SyllablePopover's effect and TWO
  // in-flight /tts fetches would both complete and start playing at
  // essentially the same moment (stop() only pauses `audioRef.current`
  // — it can't abort a fetch that hasn't yet assigned to that ref).
  const playSeqRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);

  const stop = useCallback(() => {
    // Bumping the seq invalidates any in-flight play() that hasn't yet
    // called audio.play() — belt-and-braces alongside pausing the ref.
    playSeqRef.current += 1;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    setPlaying(false);
    setLoading(false);
  }, []);

  const play = useCallback(async (text, opts = {}) => {
    if (!text) return;
    stop();
    const mySeq = ++playSeqRef.current;
    setLoading(true);
    // Iter-51.10 TTS-perf: log timing + cache status so we can diagnose
    // the "guides audio is slow" reports the same way we now diagnose
    // /stt. Numbers land in window.__ttsTimings.
    const t0 = performance.now();
    const chars = text.length;
    // Iter-51.99 — Read-to-Me word-highlighting.
    //
    // When the caller passes `withTimestamps: true` + an `onWordChange`
    // callback, we route through the new `/api/tts/with-timestamps`
    // JSON endpoint (returns MP3 base64 + per-word spans) and drive the
    // callback via a requestAnimationFrame loop watching
    // `audio.currentTime`. `currentTime` is SOURCE time, so word-sync
    // stays automatic at any HTMLAudio.playbackRate (0.3-1.2x). The
    // callback fires with the 0-based word index into `word_spans`,
    // and finally with `-1` when playback ends so the caller can clear
    // its active-word state.
    const useTimestamps = opts.withTimestamps && typeof opts.onWordChange === 'function';
    try {
      let clientRate;
      let cacheStatus = 'OFF';
      let audioBlob;
      let wordSpans = null;

      if (useTimestamps) {
        const resp = await fetch(`${API}/tts/with-timestamps`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            language: opts.language || 'en',
            speed: opts.speed ?? defaultSpeed,
            voice_gender: opts.voice_gender,
            voice: opts.voice,
            // Iter-52.15 — Starter Library caching: backend keys the
            // TTS cache on (passage_id, voice, speed, text_hash).
            // Pass-through: caller sets this to the library passage id
            // when reading a shared library passage.
            library_passage_id: opts.library_passage_id || undefined,
          }),
        });
        if (!resp.ok) { setLoading(false); return; }
        if (mySeq !== playSeqRef.current) { setLoading(false); return; }
        const j = await resp.json();
        if (mySeq !== playSeqRef.current) { setLoading(false); return; }
        clientRate = parseFloat(j.client_playback_rate || '1') || 1;
        wordSpans = Array.isArray(j.word_spans) ? j.word_spans : [];
        // Decode base64 → Blob without hitting the CDN twice.
        const byteStr = atob(j.audio_base64);
        const bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
        audioBlob = new Blob([bytes], { type: j.audio_mime || 'audio/mpeg' });
      } else {
        const resp = await fetch(`${API}/tts`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            language: opts.language || 'en',
            speed: opts.speed ?? defaultSpeed,
            voice_gender: opts.voice_gender,
            voice: opts.voice,
            // Iter-51.10: FIXED phrases (tutorial, help pages, guideline
            // read-alouds) pass cacheable:true so the server persists the
            // MP3 under /tmp/tts_cache. First tap = one-time synthesis
            // hit; every tap after = instant cache stream (X-TTS-Cache: HIT).
            cacheable: opts.cacheable === true,
          }),
        });
        if (!resp.ok) { setLoading(false); return; }
        // Iter-51.33 — if a newer play() has started while this one was
        // fetching, drop the stale response instead of racing it to the
        // audio element.
        if (mySeq !== playSeqRef.current) { setLoading(false); return; }
        cacheStatus = resp.headers.get('X-TTS-Cache') || 'OFF';
        // Iter-51.2: server clamps ElevenLabs to 0.7–1.2 natively. For slower
        // requests it returns a compensation ratio in `X-Client-Playback-Rate`
        // (e.g. 0.43 for a 0.3x request). Applying it to HTMLAudio.playbackRate
        // gives real 0.3x perceived speed with only a mild time-stretch tint.
        clientRate = parseFloat(resp.headers.get('X-Client-Playback-Rate') || '1') || 1;
        audioBlob = await resp.blob();
        if (mySeq !== playSeqRef.current) { setLoading(false); return; }
      }

      const tRespHeaders = performance.now();
      const tBlob = performance.now();
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = url;
      // Set BEFORE metadata loads so the very first playback frame is at
      // the correct rate — otherwise the child hears a brief burst at 1x
      // before the slow-down engages.
      audio.playbackRate = clientRate;
      audio.defaultPlaybackRate = clientRate;
      audioRef.current = audio;

      // Iter-51.99 — word-highlight RAF loop. Only runs when word
      // spans are provided; otherwise the loop is skipped entirely
      // and this play() is byte-for-byte identical to the legacy path.
      let rafId = null;
      let lastIdx = -1;
      const runHighlightLoop = () => {
        if (!wordSpans || wordSpans.length === 0) return;
        const tick = () => {
          if (mySeq !== playSeqRef.current) return; // stale — bail
          const el = audioRef.current;
          if (!el) return;
          const ms = el.currentTime * 1000;
          // Linear scan is fine — a chunk has ≤ ~40 words. Binary
          // search would obscure the intent for zero perf win.
          let idx = -1;
          for (let i = 0; i < wordSpans.length; i++) {
            const s = wordSpans[i];
            if (ms >= s.start_ms && ms <= s.end_ms) { idx = i; break; }
            // Between words: highlight the NEXT upcoming word so the
            // gap between words doesn't briefly clear the highlight.
            if (ms < s.start_ms) { idx = i; break; }
          }
          // If we've scanned past every span, the audio is in the
          // final decay — keep the last word highlighted until `ended`.
          if (idx === -1) idx = wordSpans.length - 1;
          if (idx !== lastIdx) {
            lastIdx = idx;
            try { opts.onWordChange(idx); } catch {}
          }
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      };

      // Resolve when playback actually FINISHES so callers that await play()
      // (e.g. the "Done for today" goodbye flow) don't navigate away before
      // the sentence completes.
      await new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          if (rafId != null) { try { cancelAnimationFrame(rafId); } catch {} rafId = null; }
          // Iter-51.99 — clear the highlight AFTER a brief pause so the
          // last word visually "settles" instead of blinking off mid-decay.
          if (useTimestamps) {
            setTimeout(() => { try { opts.onWordChange(-1); } catch {} }, 120);
          }
          // DELAYED revoke: some browsers fire `ended` at (duration - ε)
          // and revoking the blob URL synchronously in that handler cuts
          // the very last 20-50 ms of audio — heard as a stretched tail.
          // 250 ms is plenty of headroom past any real playback end.
          setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 250);
          setPlaying(false);
          resolve();
        };
        audio.onended = finish;
        audio.onerror = finish;
        // As soon as the browser can play, we flip loading→false and
        // playing→true. The two states are independent — this is the
        // key fix.
        audio.onplaying = () => {
          setLoading(false);
          setPlaying(true);
          runHighlightLoop();
          const timings = {
            chars,
            cache: cacheStatus,
            server_ms: Math.round(tRespHeaders - t0),
            blob_ms: Math.round(tBlob - tRespHeaders),
            first_frame_ms: Math.round(performance.now() - t0),
          };
          try { window.__ttsTimings = timings; } catch {}
          // eslint-disable-next-line no-console
          console.log('[tts-perf] first-frame', timings);
        };
        audio.play().catch(finish);
      });
    } finally {
      // Belt-and-braces: if the browser never fired `playing`, still
      // clear the loading state so the UI never sticks.
      setLoading(false);
      setPlaying(false);
    }
  }, [defaultSpeed, stop]);

  useEffect(() => () => stop(), [stop]);

  return { play, stop, loading, playing };
}
